import { useState, useEffect } from 'react'
import Axios from 'axios'
import LazyLoad from 'react-lazyload'
import { useTranslation } from 'next-i18next'
import { getCookie, setCookie } from '../../utils/cookies'
import dayjs from 'dayjs'
import NewShell from './NewShell'
import AboutHero from './about-new/AboutHero'
import CompanyStats from './about-new/CompanyStats'
import OriginStory from './about-new/OriginStory'
import OurGoals from './about-new/OurGoals'
import AwardsSection from './about-new/AwardsSection'
import NewsNew from '../home-new/NewsNew'
import LazyloadPlaceholder from '../ui/LazyloadPlaceholder'

// NEW-design (?new) variant of pages/about.js, rebuilt to the aboutus mockup
// (new-design-other/aboutus/single.png): Hero -> Company/stats ->
// Origin story -> Our goals -> Awards -> News. Chrome (HeaderNew/FooterNew/
// Sukhumvit font) comes from NewShell; the old /about (no ?new) page keeps
// its own separate content untouched via the guard in pages/about.js.
//
// Backend/data is carried over from the previous AboutNew.js rather than
// rebuilt: the totalParcel Axios fetch + cookie (now feeding the "พัสดุที่
// จัดการผ่านระบบ SHIPPOP" stat's CountUp instead of a hard-coded figure),
// the awards data + i18n (about-us.json's `awards-detail.*`, passed to
// AwardsSection), and — new for this page — a client-side fetch of the same
// blog feed the /new homepage uses (`/api/blog/fetch`) for the news section,
// reusing home-new's NewsNew card component as-is.
const AboutNew = () => {
  const { t } = useTranslation('about-us')

  const [totalParcel, setTotalParcel] = useState(getCookie('totalParcel') || 0)
  const [news, setNews] = useState([])

  useEffect(() => {
    const getTotalParcel = async () => {
      try {
        const res = await Axios.get('/api/total-sending-parcel')
        const resData = res?.data
        const total_Parcel = resData?.totalParcel
        const expiredDate = dayjs().add(1, 'day').toDate()
        setCookie('totalParcel', total_Parcel, { expires: expiredDate })
        setTotalParcel(getCookie('totalParcel'))
      } catch (e) {
        console.log('error', e)
      }
    }

    if (!totalParcel) {
      getTotalParcel()
    }
  }, [])

  useEffect(() => {
    const stripHtml = (s) =>
      (s || '')
        .replace(/<[^>]*>/g, '')
        .replace(/&#8230;|&hellip;/g, '…')
        .replace(/&#8211;|&ndash;/g, '–')
        .replace(/&amp;/g, '&')
        .replace(/&[^;]+;/g, ' ')
        .trim()

    const getNews = async () => {
      // Match the /new homepage exactly: fetch SHIPPOP's public WordPress blog
      // API directly (reachable + CORS-enabled in BOTH local dev and prod, and
      // produces the {title,desc,link,date,img} shape NewsNew expects). The
      // internal /api/blog/fetch proxy returns 500 in local dev, so it's only
      // a fallback — the previous code called it directly, which left the news
      // section empty here while the homepage rendered fine.
      try {
        const res = await Axios.get(
          'https://blog.shippop.com/wp-json/wp/v2/posts',
          { params: { per_page: 3, _embed: 1 }, timeout: 8000 }
        )
        setNews(
          (res.data || []).map((p) => ({
            title: stripHtml(p?.title?.rendered),
            desc: stripHtml(p?.excerpt?.rendered),
            link: p?.link,
            date: p?.date,
            img:
              p?._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
              p?.jetpack_featured_media_url ||
              null,
          }))
        )
      } catch (e) {
        try {
          const r = await Axios.get('/api/blog/fetch')
          setNews(r?.data?.data || [])
        } catch (e2) {
          console.log('news fetch error', e2)
        }
      }
    }

    getNews()
  }, [])

  const rewardsData = [
    {
      title: t('awards-detail.startup-of-the-year.title'),
      desc: t('awards-detail.startup-of-the-year.desc'),
      img: 'StartupOfTheYear.png',
    },
    {
      title: t('awards-detail.franchise-standard.title'),
      desc: t('awards-detail.franchise-standard.desc'),
      img: 'FranchiseStandard.png',
    },
    {
      title: t('awards-detail.award2021.title'),
      desc: t('awards-detail.award2021.desc'),
      img: 'award2021.png',
    },
    {
      title: t('awards-detail.ticta2020.title'),
      desc: t('awards-detail.ticta2020.desc'),
      img: 'ticta2020.png',
    },
    {
      title: t('awards-detail.red-cross.title'),
      desc: t('awards-detail.red-cross.desc'),
      img: 'redCross.jpg',
    },
    {
      title: t('awards-detail.national-inno.title'),
      desc: t('awards-detail.national-inno.desc'),
      img: 'NationalInnovation2020.jpg',
    },
    {
      title: t('awards-detail.log-tech.title'),
      desc: t('awards-detail.log-tech.desc'),
      img: 'LogTechAward2019.jpg',
    },
    {
      title: t('awards-detail.ticta.title'),
      desc: t('awards-detail.ticta.desc'),
      img: 'TICTA2019.jpg',
    },
    {
      title: t('awards-detail.apec.title'),
      desc: t('awards-detail.apec.desc'),
      img: 'APEC2018.jpg',
    },
    {
      title: t('awards-detail.gov-tech.title'),
      desc: t('awards-detail.gov-tech.desc'),
      img: 'GovTech2018.jpg',
    },
    {
      title: t('awards-detail.ais-startup.title'),
      desc: t('awards-detail.ais-startup.desc'),
      img: 'AisStartUp2017.jpg',
    },
    {
      title: t('awards-detail.tech-sauce.title'),
      desc: t('awards-detail.tech-sauce.desc'),
      img: 'Techsauce2017.jpg',
    },
    {
      title: t('awards-detail.startup-thailand.title'),
      desc: t('awards-detail.startup-thailand.desc'),
      img: 'StartupThailand.jpg',
    },
    {
      title: t('awards-detail.gsb-sme.title'),
      desc: t('awards-detail.gsb-sme.desc'),
      img: 'GSBSMEsStartup.jpg',
    },
    {
      title: t('awards-detail.rice-bowl.title'),
      desc: t('awards-detail.rice-bowl.desc'),
      img: 'RiceBowlStartupAwards2017.jpg',
    },
    {
      title: t('awards-detail.iso.title'),
      desc: t('awards-detail.iso.desc'),
      img: 'ISO9001_2015.jpg',
    },
    {
      title: t('awards-detail.best-small-franchise-2022.title'),
      desc: t('awards-detail.best-small-franchise-2022.desc'),
      img: 'Best_Small_Franchise_2022.jpg',
    },
    {
      title: t('awards-detail.apicta-2022.title'),
      desc: t('awards-detail.apicta-2022.desc'),
      img: 'APICTA_Awards_2022.jpg',
    },
    {
      title: t('awards-detail.best-small-franchise-2023.title'),
      desc: t('awards-detail.best-small-franchise-2023.desc'),
      img: 'Best_Small_Franchise_2023.jpg',
    },
  ]

  return (
    <NewShell
      title={t('new.meta.title')}
      keywords={t('new.meta.keywords')}
      description={t('new.meta.description')}
      headExtra={
        <link
          rel="preload"
          as="image"
          href="/static/img/about-us-new/hero-banner.webp"
          type="image/webp"
          fetchpriority="high"
        />
      }
    >
      <AboutHero />
      <CompanyStats totalParcel={totalParcel} />
      <LazyLoad
        offset={400}
        placeholder={<LazyloadPlaceholder />}
        once
        style={{ width: '100%' }}
      >
        <OriginStory />
        <OurGoals />
        <AwardsSection rewardsData={rewardsData} t={t} />
        <NewsNew news={news} />
      </LazyLoad>
    </NewShell>
  )
}

export default AboutNew
